import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import {
  TableContainer,
  StyledTable,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeadCell,
  LoadingContainer,
  BackgroundLoadingIndicator,
  FilterIcon,
  SortIcon,
  DragHandleCell,
  DragHandleButton,
  DragHandleIcon,
} from "./Table.styles";
import Paginator from "../Paginator";
import {
  TableProps,
  DragHandleRenderProps,
  TableCellContent,
  SpecialRowElement,
} from "./Table.types";
import { PaginatorWrapper } from "./Table.styles";
import { ProgressSpinner } from "primereact/progressspinner";
import { sortArrow, filterIcon, grabbingIcon } from "assets/icons";
import Calendar from "../Calendar/Calendar";
import useOnClickOutside from "../../hooks/useOnClickOutside";

import {
  DndContext,
  PointerSensor,
  closestCenter,
  DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import FilterCheckboxList from "../FilterCheckboxList/FilterCheckboxList";
import EmptyState from "../EmptyState";
import { reorderArray } from "../../utils/reorder";

type SortableRowDescriptor = {
  id: string;
  index: number;
  isSpecial: boolean;
  isDraggable: boolean;
};

type SortableRenderProps = {
  setNodeRef: (element: HTMLElement | null) => void;
  attributes: ReturnType<typeof useSortable>["attributes"];
  listeners: ReturnType<typeof useSortable>["listeners"];
  transform: ReturnType<typeof useSortable>["transform"];
  transition: ReturnType<typeof useSortable>["transition"];
  isDragging: boolean;
};

interface SortableTableRowProps {
  id: string;
  disabled: boolean;
  children: (props: SortableRenderProps) => React.ReactElement;
}

interface CachedTableLayout {
  columnWidths: string[];
  hasPaginator: boolean;
}

interface MeasuredTableLayout extends CachedTableLayout {
  key: string;
}

const TABLE_LAYOUT_CACHE_PREFIX = "getcourse:stable-table-layout:v1:";
const EXPANDED_CONTENT_ANIMATION_SECONDS = 0.5;
const EXPANDED_CONTENT_ANIMATION_MS = EXPANDED_CONTENT_ANIMATION_SECONDS * 1000;
const EXPANDED_CONTENT_EASING: [number, number, number, number] = [0.4, 0, 0.2, 1];

const SortableTableRow: React.FC<SortableTableRowProps> = ({ id, disabled, children }) => {
  const sortable = useSortable({ id, disabled });

  return children({
    setNodeRef: sortable.setNodeRef,
    attributes: sortable.attributes,
    listeners: sortable.listeners,
    transform: sortable.transform,
    transition: sortable.transition,
    isDragging: sortable.isDragging,
  });
};

const DefaultDragHandle: React.FC<DragHandleRenderProps> = ({
  attributes,
  listeners,
  isDragging,
  isDisabled,
}) => {
  const interactionProps = isDisabled
    ? {}
    : {
        ...(attributes ?? {}),
        ...(listeners ?? {}),
      };

  return (
    <DragHandleButton
      type="button"
      aria-label="Перетащить строку"
      data-dragging={isDragging ? "true" : undefined}
      disabled={isDisabled}
      {...interactionProps}
    >
      <DragHandleIcon as={grabbingIcon} aria-hidden="true" />
    </DragHandleButton>
  );
};

const resolveRowId = (
  row: Record<string, any>,
  index: number,
  getRowId?: TableProps["getRowId"],
) => {
  if (typeof getRowId === "function") {
    return getRowId(row, index);
  }

  if (row && (row.id !== undefined || row.id === 0)) {
    return String(row.id);
  }

  if (row && (row.key !== undefined || row.key === 0)) {
    return String(row.key);
  }

  return `row-${index}`;
};

/**
 * Корзина ширины окна для ключа кэша.
 *
 * Доли колонок снимаются один раз и живут в sessionStorage. Пока вёрстка
 * масштабируется пропорционально, доли от ширины окна не зависят — но
 * стоит браузеру упереться в минимальный кегль или --ui-scale в свой
 * нижний предел, как пропорции меняются. Без ширины в ключе замер,
 * сделанный в узком окне, оставался бы в силе и после разворачивания.
 */
const LAYOUT_WIDTH_BUCKET = 100;

const getViewportBucket = () =>
  typeof window === "undefined" ? 0 : Math.round(window.innerWidth / LAYOUT_WIDTH_BUCKET);

const buildLayoutCacheKey = (
  headers: TableProps["headers"],
  isDraggable: boolean,
  viewportBucket: number,
) => {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "server";
  const signature = headers
    .map((header) => `${header.key ?? ""}:${header.label}:${header.width ?? ""}`)
    .join("|");

  return `${TABLE_LAYOUT_CACHE_PREFIX}${pathname}:${isDraggable ? "drag" : "static"}:w${viewportBucket}:${signature}`;
};

const readCachedLayout = (key: string): CachedTableLayout | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<CachedTableLayout>;
    if (
      !Array.isArray(parsed.columnWidths) ||
      !parsed.columnWidths.every((width) => typeof width === "string") ||
      typeof parsed.hasPaginator !== "boolean"
    ) {
      return null;
    }

    return {
      columnWidths: parsed.columnWidths,
      hasPaginator: parsed.hasPaginator,
    };
  } catch {
    return null;
  }
};

const writeCachedLayout = (key: string, layout: CachedTableLayout) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(key, JSON.stringify(layout));
  } catch {
    // Private mode / storage quota must not affect table rendering.
  }
};

const sameColumnWidths = (left: string[], right: string[]) =>
  left.length === right.length && left.every((width, index) => width === right[index]);

const Table: React.FC<TableProps> = ({
  headers,
  rows,
  currentPage,
  onSort,
  onCalendarChange,
  filterStartDate,
  filterEndDate,
  handleActiveFilterChange,
  activeFilter,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  rowAmountOptions,
  isExpanded,
  loading,
  minHeight = "115px",
  specialRowKey = "title",
  expandedRowIndex,
  appliedFilters,
  onFilterSave,
  onFilterReset,
  remHeight = 134,
  isDraggable = false,
  getRowId,
  onReorder,
  renderDragHandle,
  isRowDraggable,
  emptyState,
  filteredEmptyState,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [measuredLayout, setMeasuredLayout] = useState<MeasuredTableLayout | null>(null);
  const [viewportBucket, setViewportBucket] = useState(getViewportBucket);
  const calendarRef = useRef<HTMLDivElement>(null);
  const filterListRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const expandedRowsCleanupRef = useRef<NodeJS.Timeout | null>(null);

  /*
    Внутри одной корзины ширины ничего не пересчитывается — лок ширин
    затем и нужен, чтобы колонки не дёргались. Переход в соседнюю корзину
    меняет ключ кэша, и таблица снимает пропорции заново.
  */
  useEffect(() => {
    const handleResize = () => setViewportBucket(getViewportBucket());

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useOnClickOutside(calendarRef, () => {
    if (handleActiveFilterChange) {
      handleActiveFilterChange(null);
    }
  });

  useOnClickOutside(filterListRef, () => {
    if (handleActiveFilterChange) {
      handleActiveFilterChange(null);
    }
  });

  useEffect(() => {
    const nextExpandedRowIndex =
      typeof expandedRowIndex === "number" ? expandedRowIndex : null;

    setExpandedRows((previous) => {
      const next = new Set(previous);
      if (nextExpandedRowIndex !== null) {
        next.add(nextExpandedRowIndex);
      }
      return next;
    });

    if (expandedRowsCleanupRef.current) {
      clearTimeout(expandedRowsCleanupRef.current);
    }

    expandedRowsCleanupRef.current = setTimeout(() => {
      setExpandedRows(
        nextExpandedRowIndex !== null
          ? new Set([nextExpandedRowIndex])
          : new Set(),
      );
      expandedRowsCleanupRef.current = null;
    }, EXPANDED_CONTENT_ANIMATION_MS);

    return () => {
      if (expandedRowsCleanupRef.current) {
        clearTimeout(expandedRowsCleanupRef.current);
        expandedRowsCleanupRef.current = null;
      }
    };
  }, [expandedRowIndex]);

  const visibleExpandedRows = useMemo(() => {
    const visibleRows = new Set(expandedRows);
    if (typeof expandedRowIndex === "number") {
      visibleRows.add(expandedRowIndex);
    }
    return visibleRows;
  }, [expandedRowIndex, expandedRows]);

  const openFilterList = (key: string) => {
    if (handleActiveFilterChange) {
      const newFilter = activeFilter === key ? null : key;
      handleActiveFilterChange(newFilter);
    }
  };

  const hasExpandableRows = rows.some((row) => row.expandedContent);

  const openCalendar = (key: string) => {
    if (handleActiveFilterChange) {
      const newFilter = activeFilter === key ? null : key;
      handleActiveFilterChange(newFilter);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const rowIds = useMemo(
    () => rows.map((row, index) => resolveRowId(row, index, getRowId)),
    [rows, getRowId],
  );

  const rowDescriptors = useMemo<SortableRowDescriptor[]>(
    () =>
      rows.map((row, index) => {
        const isSpecial = Boolean(
          (row[specialRowKey] as SpecialRowElement | undefined)?.props?.colSpan,
        );
        const draggableByProp = isRowDraggable ? isRowDraggable(row, index) : true;

        return {
          id: rowIds[index],
          index,
          isSpecial,
          isDraggable: isDraggable && !isSpecial && draggableByProp,
        };
      }),
    [rows, rowIds, specialRowKey, isRowDraggable, isDraggable],
  );

  const sortableRowIds = useMemo(
    () => rowDescriptors.filter((descriptor) => !descriptor.isSpecial).map((descriptor) => descriptor.id),
    [rowDescriptors],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!onReorder) {
        return;
      }

      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const sortableDescriptors = rowDescriptors.filter((descriptor) => !descriptor.isSpecial);
      const activeDescriptor = sortableDescriptors.find((descriptor) => descriptor.id === active.id);
      const overDescriptor = sortableDescriptors.find((descriptor) => descriptor.id === over.id);

      if (!activeDescriptor || !overDescriptor) {
        return;
      }

      const fromIndex = activeDescriptor.index;
      const toIndex = overDescriptor.index;

      if (fromIndex === toIndex) {
        return;
      }

      const reorderedRows = reorderArray(rows, fromIndex, toIndex);
      const reorderedIds = arrayMove(rowIds, fromIndex, toIndex);

      onReorder({
        orderedIds: reorderedIds,
        items: reorderedRows,
      });
    },
    [onReorder, rowDescriptors, rows, rowIds],
  );

  const renderHandle = useCallback(
    (props: DragHandleRenderProps) => {
      if (typeof renderDragHandle === "function") {
        return renderDragHandle(props);
      }

      return <DefaultDragHandle {...props} />;
    },
    [renderDragHandle],
  );

  const totalColumns = headers.length + (isDraggable ? 1 : 0) + (hasExpandableRows ? 1 : 0);
  const layoutCacheKey = useMemo(
    () => buildLayoutCacheKey(headers, isDraggable, viewportBucket),
    [headers, isDraggable, viewportBucket],
  );
  const cachedLayout = useMemo(() => readCachedLayout(layoutCacheKey), [layoutCacheKey]);
  const activeLayout = measuredLayout?.key === layoutCacheKey ? measuredLayout : cachedLayout;
  const lockedColumnWidths =
    activeLayout?.columnWidths.length === totalColumns ? activeLayout.columnWidths : null;
  const isBackgroundLoading = Boolean(loading && rows.length > 0);
  const shouldReservePaginator = Boolean(
    loading && totalPages <= 1 && activeLayout?.hasPaginator,
  );

  useLayoutEffect(() => {
    if (loading || rows.length === 0) {
      return;
    }

    const table = tableRef.current;
    if (!table) {
      return;
    }

    /*
      Мерить уже зафиксированную таблицу нельзя: colgroup вернёт ровно те
      доли, которые в него записаны, замер подтвердит сам себя, и колонки
      навсегда останутся в пропорциях первого рендера — даже когда окно
      развернули. Обновляем только флаг пагинатора, он замера не требует.
    */
    if (lockedColumnWidths) {
      const hasPaginator = totalPages > 1;
      if (activeLayout && activeLayout.hasPaginator !== hasPaginator) {
        const nextLayout: MeasuredTableLayout = {
          key: layoutCacheKey,
          columnWidths: activeLayout.columnWidths,
          hasPaginator,
        };
        setMeasuredLayout(nextLayout);
        writeCachedLayout(layoutCacheKey, nextLayout);
      }
      return;
    }

    const tableWidth = table.getBoundingClientRect().width;
    const headerCells = Array.from(table.querySelectorAll<HTMLTableCellElement>("thead th"));
    if (tableWidth <= 0 || headerCells.length !== totalColumns) {
      return;
    }

    const columnWidths = headerCells.map((cell) => {
      const width = cell.getBoundingClientRect().width;
      const percentage = (width / tableWidth) * 100;
      return `${percentage.toFixed(4)}%`;
    });
    const nextLayout: MeasuredTableLayout = {
      key: layoutCacheKey,
      columnWidths,
      hasPaginator: totalPages > 1,
    };

    setMeasuredLayout((previous) => {
      if (
        previous?.key === nextLayout.key &&
        previous.hasPaginator === nextLayout.hasPaginator &&
        sameColumnWidths(previous.columnWidths, nextLayout.columnWidths)
      ) {
        return previous;
      }

      return nextLayout;
    });
    writeCachedLayout(layoutCacheKey, nextLayout);
  }, [activeLayout, layoutCacheKey, loading, lockedColumnWidths, rows, totalColumns, totalPages]);

  /*
    Пустая таблица — не то же самое, что отфильтрованная в ноль.
    В первом случае уместно предложить завести запись, во втором —
    снять фильтр; раньше оба случая выглядели одинаково пустым каркасом.
  */
  const hasActiveFilters =
    Boolean(filterStartDate) ||
    Boolean(filterEndDate) ||
    Object.values(appliedFilters ?? {}).some((values) => values.length > 0);

  const isEmpty = !loading && rows.length === 0;
  const isFilteredEmpty = isEmpty && hasActiveFilters;

  const tableRows = useMemo(() => {
    if (loading && rows.length === 0) {
      return [
        <TableRow key="loading">
          <TableCell colSpan={totalColumns}>
            <LoadingContainer>
              <ProgressSpinner style={{ width: "50px", height: "50px", color: "#6A0DAD" }} />
            </LoadingContainer>
          </TableCell>
        </TableRow>,
      ];
    }

    return rows.map((row, rowIndex) => {
      const descriptor = rowDescriptors[rowIndex];
      const expandedColSpan = Math.max(totalColumns - 1, 1);
      const flushExpandedContent = Boolean(row.expandedContentFlush || isDraggable);

      if ((row[specialRowKey] as SpecialRowElement | undefined)?.props?.colSpan) {
        return <React.Fragment key={`${descriptor.id}-special`}>{row[specialRowKey] as TableCellContent}</React.Fragment>;
      }

      const isActiveExpandedRow = expandedRowIndex === rowIndex;
      const animatedExpandedContent = (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{
            height: isActiveExpandedRow ? "auto" : 0,
            opacity: isActiveExpandedRow ? 1 : 0,
          }}
          transition={{
            duration: EXPANDED_CONTENT_ANIMATION_SECONDS,
            ease: EXPANDED_CONTENT_EASING,
          }}
          style={{ overflow: "hidden" }}
        >
          {row.expandedContent as TableCellContent}
        </motion.div>
      );

      const expandedContent =
        visibleExpandedRows.has(rowIndex) && row.expandedContent ? (
          flushExpandedContent ? (
            <TableRow key={`${descriptor.id}-expanded`}>
              <TableCell colSpan={totalColumns} $expandedFlush>
                {animatedExpandedContent}
              </TableCell>
            </TableRow>
          ) : (
            <TableRow key={`${descriptor.id}-expanded`}>
              <TableCell />
              <TableCell colSpan={expandedColSpan}>{animatedExpandedContent}</TableCell>
            </TableRow>
          )
        ) : null;

      if (!isDraggable) {
        return (
          <React.Fragment key={descriptor.id}>
            <TableRow isExpanded={isActiveExpandedRow}>
              {headers.map((header, colIndex) => (
                <TableCell
                  key={colIndex}
                  isHidden={
                    isActiveExpandedRow &&
                    header.hidden === true &&
                    !row.isDeleted
                  }
                >
                  <div className="content">{header.key ? (row[header.key] as TableCellContent) : null}</div>
                </TableCell>
              ))}
            </TableRow>
            {expandedContent}
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={descriptor.id}>
          <SortableTableRow id={descriptor.id} disabled={!descriptor.isDraggable}>
            {({
              setNodeRef,
              attributes,
              listeners,
              transform,
              transition,
              isDragging,
            }) => {
              const style: React.CSSProperties = {
                transform: transform ? CSS.Transform.toString(transform) : undefined,
                transition,
              };

              return (
                <TableRow ref={setNodeRef} isExpanded={isActiveExpandedRow} style={style}>
                  <DragHandleCell data-disabled={!descriptor.isDraggable}>
                    <div className="content">
                      {renderHandle({
                        row,
                        index: rowIndex,
                        attributes,
                        listeners,
                        isDragging,
                        isDisabled: !descriptor.isDraggable,
                      })}
                    </div>
                  </DragHandleCell>
                  {headers.map((header, colIndex) => (
                    <TableCell
                      key={colIndex}
                      isHidden={
                        isActiveExpandedRow &&
                        header.hidden === true &&
                        !row.isDeleted
                      }
                    >
                      <div className="content">{header.key ? (row[header.key] as TableCellContent) : null}</div>
                    </TableCell>
                  ))}
                </TableRow>
              );
            }}
          </SortableTableRow>
          {expandedContent}
        </React.Fragment>
      );
    });
  }, [
    expandedRowIndex,
    visibleExpandedRows,
    headers,
    isDraggable,    loading,
    renderHandle,
    rowDescriptors,
    rows,
    specialRowKey,
    totalColumns,
  ]);

  const renderedTableBody = isFilteredEmpty ? (
    <TableRow>
      <TableCell colSpan={totalColumns} $expandedFlush>
        <EmptyState kind="filtered" {...filteredEmptyState} />
      </TableCell>
    </TableRow>
  ) : (
    tableRows
  );

  return (
    <TableContainer isExpanded={isExpanded} minHeight={minHeight} remHeight={remHeight}>
      {isBackgroundLoading && (
        <BackgroundLoadingIndicator role="status" aria-label="Обновление таблицы">
          <ProgressSpinner style={{ width: "24px", height: "24px", color: "#6A0DAD" }} />
        </BackgroundLoadingIndicator>
      )}
      {isEmpty && !isFilteredEmpty ? (
        <EmptyState kind="empty" {...emptyState} />
      ) : (
        <StyledTable
          ref={tableRef}
          $layoutLocked={Boolean(lockedColumnWidths)}
          aria-busy={loading ? "true" : "false"}
        >
          {lockedColumnWidths && (
            <colgroup>
              {/*
                Объявленная ширина сильнее замера. Замер делается по первому
                кадру с данными, а часть ячеек к этому моменту ещё пуста —
                например, иконки действий ждут ответа о подписке. Колонка,
                измеренная пустой, так и осталась бы узкой.
              */}
              {lockedColumnWidths.map((width, index) => {
                const declaredWidth = headers[index - (isDraggable ? 1 : 0)]?.width;

                return <col key={index} style={{ width: declaredWidth ?? width }} />;
              })}
            </colgroup>
          )}
          <TableHeader>
            <tr>
              {isDraggable && (
                <th
                  style={{ width: lockedColumnWidths ? undefined : "64px" }}
                  aria-hidden="true"
                />
              )}
              {headers.map((header, index) => (
                <th
                  key={index}
                  style={{
                    width: lockedColumnWidths ? undefined : header.width,
                    position: "relative",
                  }}
                >
                  <TableHeadCell
                    key={index}
                    onClick={
                      header.sortable !== false
                        ? () => onSort?.(header.fieldName || "")
                        : undefined
                    }
                    style={{
                      cursor: header.sortable === false ? "default" : "pointer",
                      width: lockedColumnWidths ? undefined : headers[index].width,
                    }}
                  >
                    {header.sortable !== false && header.key && (
                      <SortIcon as={sortArrow} aria-hidden="true" />
                    )}

                    <span>{header.label}</span>

                    {header.isFilter && header.filterType === "calendar" && (
                      <FilterIcon
                        as={filterIcon}
                        aria-hidden="true"
                        style={{ cursor: "pointer", marginLeft: "8px" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openCalendar(header.key || "");
                        }}
                      />
                    )}

                    {header.isFilter && header.filterType === "list" && (
                      <FilterIcon
                        as={filterIcon}
                        aria-hidden="true"
                        style={{ cursor: "pointer", marginLeft: "8px" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openFilterList(header.key || "");
                        }}
                      />
                    )}
                  </TableHeadCell>
                  {header.isFilter && header.filterType === "calendar" && (
                    <>
                      {activeFilter === header.key && (
                        <div
                          ref={calendarRef}
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            zIndex: 10,
                          }}
                        >
                          <Calendar
                            startDate={filterStartDate || undefined}
                            endDate={filterEndDate || undefined}
                            isRange={true}
                            onDateChange={onCalendarChange}
                          />
                        </div>
                      )}
                    </>
                  )}
                  {header.isFilter && header.filterType === "list" && (
                    <>
                      {activeFilter === header.key && (
                        <div
                          ref={filterListRef}
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            zIndex: 10,
                          }}
                        >
                          <FilterCheckboxList
                            options={header.listOptions ?? []}
                            selectedOptions={appliedFilters?.[header.key || ""] || []}
                            onSave={(selected) => onFilterSave?.(header.key || "", selected)}
                            onReset={() => onFilterReset?.(header.key || "")}
                            title={header.listFilter?.title ?? header.label}
                            searchable={header.listFilter?.searchable ?? false}
                            searchPlaceholder={
                              header.listFilter?.searchPlaceholder ??
                              `Найти ${header.label.toLocaleLowerCase("ru-RU")}`
                            }
                            emptyMessage={header.listFilter?.emptyMessage}
                          />
                        </div>
                      )}
                    </>
                  )}
                </th>
              ))}
              {hasExpandableRows && <th />}
            </tr>
          </TableHeader>
          {isDraggable && !loading ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sortableRowIds} strategy={verticalListSortingStrategy}>
                <TableBody>{renderedTableBody}</TableBody>
              </SortableContext>
            </DndContext>
          ) : (
            <TableBody>{renderedTableBody}</TableBody>
          )}
        </StyledTable>
      )}
      {(totalPages > 1 || shouldReservePaginator) && (
        <PaginatorWrapper
          aria-hidden={shouldReservePaginator ? "true" : undefined}
          style={
            shouldReservePaginator
              ? { visibility: "hidden", pointerEvents: "none" }
              : undefined
          }
        >
          <Paginator
            currentPage={shouldReservePaginator ? 1 : currentPage}
            totalPages={shouldReservePaginator ? 2 : totalPages}
            onPageChange={shouldReservePaginator ? () => {} : onPageChange}
            pageSize={pageSize}
            onPageSizeChange={shouldReservePaginator ? () => {} : onPageSizeChange}
            rowAmountOptions={rowAmountOptions}
          />
        </PaginatorWrapper>
      )}
    </TableContainer>
  );
};

export default Table;
